import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Toast {
  id: number;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  show: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toasts = new BehaviorSubject<Toast[]>([]);
  public toasts$ = this.toasts.asObservable();
  private toastId = 0;

  show(type: 'success' | 'error' | 'warning' | 'info', title: string, message: string, duration: number = 5000) {
    const toast: Toast = {
      id: ++this.toastId,
      type,
      title,
      message,
      duration,
      show: true
    };

    const currentToasts = this.toasts.value;
    this.toasts.next([...currentToasts, toast]);

    // Auto remove after duration
    setTimeout(() => {
      this.remove(toast.id);
    }, duration);

    return toast.id;
  }

  success(title: string, message: string, duration?: number) {
    return this.show('success', title, message, duration);
  }

  error(title: string, message: string, duration?: number) {
    return this.show('error', title, message, duration);
  }

  warning(title: string, message: string, duration?: number) {
    return this.show('warning', title, message, duration);
  }

  info(title: string, message: string, duration?: number) {
    return this.show('info', title, message, duration);
  }

  // Helper method to show API error responses
  showApiError(error: any, defaultMessage: string = 'An error occurred') {
    let errorMessage = defaultMessage;
    let errorTitle = 'Error';

    // Try to extract error message from different possible response structures
    if (error?.error?.message) {
      errorMessage = error.error.message;
    } else if (error?.error?.data?.message) {
      errorMessage = error.error.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    // Try to extract error title
    if (error?.error?.title) {
      errorTitle = error.error.title;
    } else if (error?.error?.data?.title) {
      errorTitle = error.error.data.title;
    }

    return this.error(errorTitle, errorMessage);
  }

  // Helper method to show API success responses
  showApiSuccess(response: any, defaultMessage: string = 'Operation completed successfully') {
    let successMessage = defaultMessage;
    let successTitle = 'Success';

    // Try to extract success message from response
    if (response?.message) {
      successMessage = response.message;
    } else if (response?.data?.message) {
      successMessage = response.data.message;
    }

    // Try to extract success title
    if (response?.title) {
      successTitle = response.title;
    } else if (response?.data?.title) {
      successTitle = response.data.title;
    }

    return this.success(successTitle, successMessage);
  }

  remove(id: number) {
    const currentToasts = this.toasts.value;
    const updatedToasts = currentToasts.map(toast =>
      toast.id === id ? { ...toast, show: false } : toast
    );
    this.toasts.next(updatedToasts);

    // Remove from array after animation
    setTimeout(() => {
      const filteredToasts = currentToasts.filter(toast => toast.id !== id);
      this.toasts.next(filteredToasts);
    }, 300);
  }

  clear() {
    this.toasts.next([]);
  }
}
